/**
 * Graph validation utilities for workflow validation
 *
 * These functions work with both frontend and backend data structures
 * by accepting minimal interface types.
 */

import type {
  UnconnectedSocket,
  ValidationStep,
  ValidationEdge,
  ValidationResult,
  ValidationError,
  ValidationWarning,
} from "./types";
import { FLOW_IN_SOCKET_ID } from "../types";
import { analyseFanoutTopology } from "./fanout-topology";
import { findFanInSaveErrors } from "./fan-in-topology";
import { findTaskAnchorBindingErrors } from "./task-anchor-binding";
import { detectCycles } from "./cycles";

/**
 * Find all nodes that are not connected to any other node
 * A node is "floating" if it has no incoming AND no outgoing edges
 *
 * @param steps - All steps in the workflow
 * @param edges - All edges in the workflow
 * @returns Array of step IDs that are floating (completely disconnected)
 */
export function findFloatingNodes(
  steps: ValidationStep[],
  edges: ValidationEdge[],
): string[] {
  // Build sets of nodes that have connections
  const connectedNodes = new Set<string>();

  for (const edge of edges) {
    connectedNodes.add(edge.sourceStepId);
    connectedNodes.add(edge.targetStepId);
  }

  // Find nodes with no connections at all
  const floatingNodes: string[] = [];
  for (const step of steps) {
    if (!connectedNodes.has(step.id)) {
      floatingNodes.push(step.id);
    }
  }

  return floatingNodes;
}

/**
 * Find all required input sockets that nothing feeds.
 *
 * Three things count as feeding a socket, because all three are resolved before
 * the handler runs: an incoming edge, a static value in config, and a
 * `manualOverride` socket expression. The expression case is not a nicety — such
 * an expression is the ONLY way to feed a socket a composed value (a
 * `string_join` of several source columns, a `fallback` for a nullable one), and
 * no edge can express it. Counting only edges reported those sockets as
 * unconnected, which made a working graph unsavable as active.
 *
 * The `manualOverride` half of that is equally load-bearing in the other
 * direction: an expression WITHOUT the flag is ignored at run time, so counting
 * it here would wave through a graph whose step can never satisfy its AND-join.
 *
 * Note: Flow control sockets (__flow_control_in__) are excluded from this check
 * as they are implicit and not required to be connected.
 *
 * @param steps - All steps in the workflow
 * @param edges - All edges in the workflow
 * @returns Array of unconnected required sockets
 */
export function findUnconnectedRequiredSockets(
  steps: ValidationStep[],
  edges: ValidationEdge[],
): UnconnectedSocket[] {
  // Build a set of connected input sockets (format: "stepId:socketId")
  const connectedSockets = new Set<string>();

  for (const edge of edges) {
    connectedSockets.add(`${edge.targetStepId}:${edge.targetSocketId}`);
  }

  // Find required input sockets without connections
  const unconnectedSockets: UnconnectedSocket[] = [];

  for (const step of steps) {
    // Static inputs from config satisfy required sockets without wiring.
    // Stored under the reserved key "__static_inputs" (system-level config).
    const rawStaticInputs = step.config?.["__static_inputs"];
    const staticInputs =
      rawStaticInputs &&
      typeof rawStaticInputs === "object" &&
      !Array.isArray(rawStaticInputs)
        ? (rawStaticInputs as Record<string, unknown>)
        : {};

    for (const socket of step.sockets.inputs) {
      // Skip flow control sockets - they are implicit and optional
      if (socket.id === FLOW_IN_SOCKET_ID) {
        continue;
      }

      if (socket.required) {
        const key = `${step.id}:${socket.id}`;
        const hasEdge = connectedSockets.has(key);
        const hasStaticValue = staticInputs[socket.id] !== undefined;
        // `manualOverride` is REQUIRED, not incidental. `graphTraversal`
        // resolves a socket from its expression only when the flag is set
        // (`if (!socket.expression || !socket.manualOverride) continue;`), so a
        // stale or edge-derived expression left on an unwired socket feeds it
        // NOTHING at run time. Accepting any nonblank expression here let such a
        // graph publish as active and then never satisfy the step's AND-join —
        // the step simply never runs, with no error to read.
        const hasExpression =
          socket.manualOverride === true &&
          (socket.expression ?? "").trim() !== "";
        if (!hasEdge && !hasStaticValue && !hasExpression) {
          unconnectedSockets.push({
            stepId: step.id,
            socketId: socket.id,
            socketName: socket.name,
          });
        }
      }
    }
  }

  return unconnectedSockets;
}

/**
 * Count the number of trigger nodes in the workflow
 *
 * @param steps - All steps in the workflow
 * @returns Number of trigger nodes
 */
export function countTriggerNodes(steps: ValidationStep[]): number {
  return steps.filter((step) => step.category === "Trigger").length;
}

/**
 * Validate step configs against their Zod schemas.
 *
 * @param steps - Steps with their current configs
 * @param configSchemas - Map of step type → Zod configSchema (import from config-schemas.ts)
 * @returns Array of validation errors for steps with invalid configs
 */
export function validateStepConfigs(
  steps: Array<{
    id: string;
    type: string;
    label: string;
    config: Record<string, unknown>;
  }>,
  configSchemas: Record<
    string,
    {
      safeParse: (data: unknown) => {
        success: boolean;
        error?: {
          issues: Array<{ path: PropertyKey[]; message: string }>;
        };
      };
    }
  >,
): ValidationError[] {
  const errors: ValidationError[] = [];

  for (const step of steps) {
    const schema = configSchemas[step.type];
    if (!schema) continue; // Unknown step type — skip

    const result = schema.safeParse(step.config);
    if (!result.success && result.error) {
      const issues = result.error.issues
        .map((issue) => {
          const path = issue.path.join(".");
          return path ? `${path}: ${issue.message}` : issue.message;
        })
        .join("; ");

      errors.push({
        code: "INVALID_STEP_CONFIG",
        message: `"${step.label}" has invalid configuration: ${issues}`,
        nodeIds: [step.id],
      });
    }
  }

  return errors;
}

/**
 * Find edges where source and target socket types are incompatible.
 *
 * @param edges - Edges with socket type info
 * @param isCompatible - Compatibility check function (injected to avoid coupling to FE logic)
 * @returns Array of validation errors for incompatible edges
 */
export function findIncompatibleEdges(
  edges: Array<{
    id: string;
    sourceStepId: string;
    targetStepId: string;
    sourceSocketType: string;
    targetSocketType: string;
    sourceLabel?: string;
    targetLabel?: string;
  }>,
  isCompatible: (source: string, target: string) => boolean,
): ValidationError[] {
  const errors: ValidationError[] = [];

  for (const edge of edges) {
    if (!isCompatible(edge.sourceSocketType, edge.targetSocketType)) {
      const from = edge.sourceLabel ?? edge.sourceStepId;
      const to = edge.targetLabel ?? edge.targetStepId;
      errors.push({
        code: "INCOMPATIBLE_EDGE",
        message: `Incompatible connection from "${from}" (${edge.sourceSocketType}) to "${to}" (${edge.targetSocketType})`,
        nodeIds: [edge.sourceStepId, edge.targetStepId],
      });
    }
  }

  return errors;
}

/**
 * Find steps holding two sockets with the same id, in either direction.
 *
 * A socket id is not just a label: the engine reads an upstream step's payload
 * with it (`sourceOutputData[edge.sourceSocketId]`), edges reference it as a
 * plain string with no FK, and stored `(output ...)` expressions embed it. Two
 * sockets sharing an id therefore make an edge ambiguous — one of them is
 * unreachable, and which one wins is an implementation detail of whichever code
 * last built the list.
 *
 * The generators refuse to emit a duplicate, so this catches definitions saved
 * before those guards existed. Reported per step, listing each repeated id.
 */
export function findDuplicateSocketIds(
  steps: ValidationStep[],
): Array<{ stepId: string; socketIds: string[] }> {
  const findings: Array<{ stepId: string; socketIds: string[] }> = [];

  for (const step of steps) {
    // Inputs and outputs are separate namespaces — a step may legitimately have
    // an input and an output sharing an id (e.g. read_frame's column filters
    // and its column outputs) — so each side is checked on its own.
    const duplicates = new Set<string>();
    for (const sockets of [step.sockets.inputs, step.sockets.outputs]) {
      const seen = new Set<string>();
      for (const socket of sockets) {
        if (seen.has(socket.id)) duplicates.add(socket.id);
        seen.add(socket.id);
      }
    }
    if (duplicates.size > 0) {
      findings.push({ stepId: step.id, socketIds: [...duplicates].sort() });
    }
  }

  return findings;
}

/**
 * Validate a workflow graph
 *
 * This performs all validation checks and returns a combined result.
 *
 * @param steps - All steps in the workflow
 * @param edges - All edges in the workflow
 * @param options - Validation options
 * @returns ValidationResult with all errors found
 */
export function validateWorkflow(
  steps: ValidationStep[],
  edges: ValidationEdge[],
  options: {
    /** If true, unconnected required sockets are blocking errors */
    requireConnectedSockets?: boolean;
  } = {},
): ValidationResult {
  const errors: ValidationError[] = [];

  // Check for cycles
  const cycleResult = detectCycles(steps, edges);
  if (cycleResult.hasCycle) {
    errors.push({
      code: "CYCLE_DETECTED",
      message: "Workflow contains a cycle which would cause infinite execution",
      nodeIds: cycleResult.cycleNodeIds,
    });
  }

  // Check for floating nodes
  const floatingNodes = findFloatingNodes(steps, edges);
  if (floatingNodes.length > 0) {
    errors.push({
      code: "FLOATING_NODE",
      message: `${floatingNodes.length} node${floatingNodes.length > 1 ? "s are" : " is"} not connected to the workflow`,
      nodeIds: floatingNodes,
    });
  }

  // Check for steps carrying two sockets with the same id. Always blocking:
  // unlike an unconnected input this is not a work-in-progress state, it is a
  // graph whose edges cannot be resolved unambiguously.
  const duplicateSockets = findDuplicateSocketIds(steps);
  if (duplicateSockets.length > 0) {
    errors.push({
      code: "DUPLICATE_SOCKET_ID",
      message: duplicateSockets
        .map(
          (finding) =>
            `Step has more than one socket with id ${finding.socketIds
              .map((id) => `"${id}"`)
              .join(", ")}`,
        )
        .join("; "),
      nodeIds: duplicateSockets.map((finding) => finding.stepId),
      sockets: duplicateSockets.flatMap((finding) =>
        finding.socketIds.map((socketId) => ({
          stepId: finding.stepId,
          socketId,
        })),
      ),
    });
  }

  // Await (fan_in) topology. The structural rules are always blocking: unlike a
  // half-wired graph in progress, a fan-in in the wrong place does not run at
  // all — see fan-in-topology.ts. The save wrapper drops the unconnected-Value
  // error, which the required-socket pass below already owns; emitting it here
  // rejected a freshly dropped Await even with `requireConnectedSockets` off,
  // and named the same socket twice with it on.
  errors.push(...findFanInSaveErrors(steps, edges));

  // Task steps whose wired anchor and selected task type disagree. Always
  // blocking: the step fails on every run until its config changes.
  errors.push(...findTaskAnchorBindingErrors(steps, edges));

  // Check for unconnected required sockets (only if required)
  if (options.requireConnectedSockets) {
    const unconnectedSockets = findUnconnectedRequiredSockets(steps, edges);
    if (unconnectedSockets.length > 0) {
      errors.push({
        code: "UNCONNECTED_REQUIRED_SOCKET",
        message: `${unconnectedSockets.length} required input${unconnectedSockets.length > 1 ? "s are" : " is"} not connected`,
        sockets: unconnectedSockets.map((s) => ({
          stepId: s.stepId,
          socketId: s.socketId,
        })),
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings: findFanoutWarnings(steps, edges),
  };
}

/**
 * Non-blocking findings about fanout shape.
 *
 * Deliberately separate from `errors`: these graphs are valid and run
 * correctly. They just scale in a way that is invisible until an unusually
 * large input arrives. Never let one of these fail a publish.
 */
export function findFanoutWarnings(
  steps: ValidationStep[],
  edges: ValidationEdge[],
): ValidationWarning[] {
  const nameOf = (stepId: string): string => {
    const step = steps.find((candidate) => candidate.id === stepId);
    return step?.name ?? step?.type ?? stepId;
  };

  const warnings: ValidationWarning[] = [];

  for (const finding of analyseFanoutTopology(steps, edges)) {
    const { boundary } = finding;
    const target = nameOf(boundary.targetStepId);
    const width =
      boundary.knownWidth !== null ? ` (${boundary.knownWidth} items)` : "";

    if (finding.nestedBoundaryTargetIds.length > 0) {
      warnings.push({
        code: "NESTED_FANOUT",
        message:
          `"${target}" fans out${width}, and ${finding.nestedBoundaryTargetIds.length} ` +
          (finding.nestedBoundaryTargetIds.length > 1
            ? "further fanouts run"
            : "further fanout runs") +
          ` inside it — child counts multiply, they do not add.`,
        nodeIds: [boundary.targetStepId, ...finding.nestedBoundaryTargetIds],
        detail:
          `Each item entering "${target}" starts a child workflow, and each of those ` +
          `starts more, so the two widths multiply rather than add` +
          (boundary.knownWidth !== null
            ? ` — ${boundary.knownWidth} items here, times however many the inner fanout produces`
            : "") +
          `. An Await (fan_in) between them ends the outer region so the two run in ` +
          `sequence instead of nested, taking peak concurrency from N x M down to ` +
          `max(N, M) — note the join hands downstream one entry per child, so the ` +
          `second fanout then runs over cohorts rather than individual items. ` +
          `Otherwise, scope the inner fanout to its parent's slice or flatten it ` +
          `into a single level.`,
      });
    }

    if (finding.invariantStepIds.length > 0) {
      const count = finding.invariantStepIds.length;
      warnings.push({
        code: "FANOUT_INVARIANT_STEPS",
        message:
          `${count} of ${finding.branchStepIds.length} steps inside the "${target}" ` +
          `fanout never ${count === 1 ? "uses" : "use"} the item being fanned out.`,
        nodeIds: finding.invariantStepIds,
        detail:
          `${count === 1 ? "This step receives" : "These steps receive"} the same inputs ` +
          `in every child, so the work is repeated once per item. Either ` +
          `${count === 1 ? "it belongs" : "they belong"} upstream of the fanout, or ` +
          `${count === 1 ? "it depends" : "they depend"} on the fanned item through the ` +
          `database rather than a socket — which the engine cannot see, so it cannot give ` +
          `each child its own slice. A step-version mismatch is a common cause: check ` +
          `whether a variant exists that takes the fanned item directly as an input.`,
      });
    }
  }

  return warnings;
}
